/**
 * Generated by orval v8.23.0 🍺
 * Do not edit manually.
 * Resource and Data Exchange
 * Resource Explorer Service API
 * OpenAPI spec version: 0.1.0
 */
import type {HttpContext, HttpEvent, HttpParams} from '@angular/common/http';
import {HttpResponse as AngularHttpResponse, HttpClient, HttpHeaders} from '@angular/common/http';

import {Injectable, inject} from '@angular/core';

import {Observable} from 'rxjs';

import {APP_ENVIRONMENT} from '@app/core/tokens/app-environment.token';
import {type AppEnvironment} from '@models/environment';

import type {
  FavoriteResourceParams,
  FileResponse,
  GetResourcesParams,
  RequestBodyFileUpload,
  ResourceCreate,
  ResourceCreateOptional,
  ResourceResponse,
} from '../../models';

interface HttpClientOptions {
  readonly headers?: HttpHeaders | Record<string, string | string[]>;
  readonly context?: HttpContext;
  readonly params?: HttpParams | Record<string, string | number | boolean | Array<string | number | boolean>>;
  readonly reportProgress?: boolean;
  readonly withCredentials?: boolean;
  readonly credentials?: RequestCredentials;
  readonly keepalive?: boolean;
  readonly priority?: RequestPriority;
  readonly cache?: RequestCache;
  readonly mode?: RequestMode;
  readonly redirect?: RequestRedirect;
  readonly referrer?: string;
  readonly integrity?: string;
  readonly referrerPolicy?: ReferrerPolicy;
  readonly transferCache?: {includeHeaders?: string[]} | boolean;
  readonly timeout?: number;
}

type HttpClientBodyOptions = HttpClientOptions & {
  readonly observe?: 'body';
};

type HttpClientEventOptions = HttpClientOptions & {
  readonly observe: 'events';
};

type HttpClientResponseOptions = HttpClientOptions & {
  readonly observe: 'response';
};

type HttpClientObserveOptions = HttpClientOptions & {
  readonly observe?: 'body' | 'events' | 'response';
};

type AngularHttpParamValue = string | number | boolean | Array<string | number | boolean>;
type AngularHttpParamValueWithNullable = AngularHttpParamValue | null;

function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys?: ReadonlySet<string>,
  preserveRequiredNullables?: false,
  passthroughKeys?: undefined,
): Record<string, AngularHttpParamValue>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> | undefined,
  preserveRequiredNullables: true,
  passthroughKeys?: undefined,
): Record<string, AngularHttpParamValueWithNullable>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> | undefined,
  preserveRequiredNullables: boolean | undefined,
  passthroughKeys: ReadonlySet<string>,
): Record<string, unknown>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> = new Set(),
  preserveRequiredNullables = false,
  passthroughKeys: ReadonlySet<string> = new Set(),
): Record<string, unknown> {
  const filteredParams: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (passthroughKeys.has(key)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
      continue;
    }
    if (Array.isArray(value)) {
      const filtered = value.filter(
        item => item != null && (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'),
      ) as Array<string | number | boolean>;
      if (filtered.length) {
        filteredParams[key] = filtered;
      }
    } else if (value === null && requiredNullableKeys.has(key)) {
      // With a paramsSerializer (preserveRequiredNullables) the literal null
      // is passed through for it to consume; without one, emit an empty
      // string so the required key still reaches the wire as `?key=`
      // instead of being silently dropped. See #3712.
      filteredParams[key] = preserveRequiredNullables ? null : '';
    } else if (
      value != null &&
      (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    ) {
      filteredParams[key] = value;
    }
  }
  return filteredParams;
}

@Injectable({providedIn: 'root'})
export class ResourceService {
  public http = inject(HttpClient);
  _environment: AppEnvironment = inject(APP_ENVIRONMENT);
  _baseUrl: string | undefined = undefined;

  get baseUrl(): string {
    return this._baseUrl ?? this._environment.api;
  }

  set baseUrl(newUrl: string) {
    this._baseUrl = newUrl;
  }

  /**
   * Gets a list of all viewable resources
   *
   * Query Parameters:
   * - `page`: The page number to retrieve. All resources are returned if not provided.
   * - `per_page`: The number of items per page. All resources are returned if not provided.
   * - `org_curated_for`: Show resources curated specifically for a given organization
   * - `category_id`: Show resources that belong to a specific tertiary category
   * - `view_favorites`: Show resources favorited by the requesting user
   * - `view_owned`: Show resources owned by the requesting user
   * @summary Get Resources
   */
  getResources<TData = ResourceResponse[]>(
    params?: GetResourcesParams,
    options?: HttpClientBodyOptions,
  ): Observable<TData>;
  getResources<TData = ResourceResponse[]>(
    params?: GetResourcesParams,
    options?: HttpClientEventOptions,
  ): Observable<HttpEvent<TData>>;
  getResources<TData = ResourceResponse[]>(
    params?: GetResourcesParams,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  getResources<TData = ResourceResponse[]>(
    params?: GetResourcesParams,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const filteredParams = filterParams({...params, ...options?.params}, new Set<string>([]));

    if (options?.observe === 'events') {
      return this.http.get<TData>(`${this.baseUrl}/api/resources`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
        params: filteredParams,
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${this.baseUrl}/api/resources`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
        params: filteredParams,
      });
    }

    return this.http.get<TData>(`${this.baseUrl}/api/resources`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
      params: filteredParams,
    });
  }
  /**
   * Creates a new resource
   * @summary Create Resource
   */
  createResource<TData = unknown | void>(
    resourceCreate: ResourceCreate,
    options?: HttpClientBodyOptions,
  ): Observable<TData>;
  createResource<TData = unknown | void>(
    resourceCreate: ResourceCreate,
    options?: HttpClientEventOptions,
  ): Observable<HttpEvent<TData>>;
  createResource<TData = unknown | void>(
    resourceCreate: ResourceCreate,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  createResource<TData = unknown | void>(
    resourceCreate: ResourceCreate,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.post<TData>(`${this.baseUrl}/api/resources`, resourceCreate, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.post<TData>(`${this.baseUrl}/api/resources`, resourceCreate, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.post<TData>(`${this.baseUrl}/api/resources`, resourceCreate, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Deletes resource permanently.
   * @summary Delete Resource
   */
  deleteResource<TData = unknown>(resourceId: string, options?: HttpClientBodyOptions): Observable<TData>;
  deleteResource<TData = unknown>(resourceId: string, options?: HttpClientEventOptions): Observable<HttpEvent<TData>>;
  deleteResource<TData = unknown>(
    resourceId: string,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  deleteResource<TData = unknown>(
    resourceId: string,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.delete<TData>(`${this.baseUrl}/api/resources/${resourceId}`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.delete<TData>(`${this.baseUrl}/api/resources/${resourceId}`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.delete<TData>(`${this.baseUrl}/api/resources/${resourceId}`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Gets metadata for a specified (resource_id) resource
   * @summary Get Resource
   */
  getResource<TData = ResourceResponse>(resourceId: string, options?: HttpClientBodyOptions): Observable<TData>;
  getResource<TData = ResourceResponse>(
    resourceId: string,
    options?: HttpClientEventOptions,
  ): Observable<HttpEvent<TData>>;
  getResource<TData = ResourceResponse>(
    resourceId: string,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  getResource<TData = ResourceResponse>(
    resourceId: string,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.get<TData>(`${this.baseUrl}/api/resources/${resourceId}`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${this.baseUrl}/api/resources/${resourceId}`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.get<TData>(`${this.baseUrl}/api/resources/${resourceId}`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Patches an existing resource. This endpoint allows updates by sending a single field to update in the request
   * body.
   * @summary Patch Resource
   */
  patchResource<TData = unknown | void>(
    resourceId: string,
    resourceCreateOptional: ResourceCreateOptional,
    options?: HttpClientBodyOptions,
  ): Observable<TData>;
  patchResource<TData = unknown | void>(
    resourceId: string,
    resourceCreateOptional: ResourceCreateOptional,
    options?: HttpClientEventOptions,
  ): Observable<HttpEvent<TData>>;
  patchResource<TData = unknown | void>(
    resourceId: string,
    resourceCreateOptional: ResourceCreateOptional,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  patchResource<TData = unknown | void>(
    resourceId: string,
    resourceCreateOptional: ResourceCreateOptional,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.patch<TData>(`${this.baseUrl}/api/resources/${resourceId}`, resourceCreateOptional, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.patch<TData>(`${this.baseUrl}/api/resources/${resourceId}`, resourceCreateOptional, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.patch<TData>(`${this.baseUrl}/api/resources/${resourceId}`, resourceCreateOptional, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Updates an existing resource
   * @summary Update Resource
   */
  updateResource<TData = unknown | void>(
    resourceId: string,
    resourceCreate: ResourceCreate,
    options?: HttpClientBodyOptions,
  ): Observable<TData>;
  updateResource<TData = unknown | void>(
    resourceId: string,
    resourceCreate: ResourceCreate,
    options?: HttpClientEventOptions,
  ): Observable<HttpEvent<TData>>;
  updateResource<TData = unknown | void>(
    resourceId: string,
    resourceCreate: ResourceCreate,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  updateResource<TData = unknown | void>(
    resourceId: string,
    resourceCreate: ResourceCreate,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.put<TData>(`${this.baseUrl}/api/resources/${resourceId}`, resourceCreate, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.put<TData>(`${this.baseUrl}/api/resources/${resourceId}`, resourceCreate, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.put<TData>(`${this.baseUrl}/api/resources/${resourceId}`, resourceCreate, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Updates a resource by adding the requesting user to the list of users who have added it to their favorites.
   * Set query param `remove` to `true` to remove the requesting user from the list.
   * @summary Favorite Resource
   */
  favoriteResource<TData = unknown | void>(
    resourceId: string,
    params?: FavoriteResourceParams,
    options?: HttpClientBodyOptions,
  ): Observable<TData>;
  favoriteResource<TData = unknown | void>(
    resourceId: string,
    params?: FavoriteResourceParams,
    options?: HttpClientEventOptions,
  ): Observable<HttpEvent<TData>>;
  favoriteResource<TData = unknown | void>(
    resourceId: string,
    params?: FavoriteResourceParams,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  favoriteResource<TData = unknown | void>(
    resourceId: string,
    params?: FavoriteResourceParams,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const filteredParams = filterParams({...params, ...options?.params}, new Set<string>([]));

    if (options?.observe === 'events') {
      return this.http.put<TData>(`${this.baseUrl}/api/resources/${resourceId}/favorite`, undefined, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
        params: filteredParams,
      });
    }

    if (options?.observe === 'response') {
      return this.http.put<TData>(`${this.baseUrl}/api/resources/${resourceId}/favorite`, undefined, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
        params: filteredParams,
      });
    }

    return this.http.put<TData>(`${this.baseUrl}/api/resources/${resourceId}/favorite`, undefined, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
      params: filteredParams,
    });
  }
  /**
   * Uploads a new file to a resource
   * @summary Upload Resource File
   */
  uploadResourceFile<TData = FileResponse>(
    resourceId: string,
    requestBodyFileUpload: RequestBodyFileUpload,
    options?: HttpClientBodyOptions,
  ): Observable<TData>;
  uploadResourceFile<TData = FileResponse>(
    resourceId: string,
    requestBodyFileUpload: RequestBodyFileUpload,
    options?: HttpClientEventOptions,
  ): Observable<HttpEvent<TData>>;
  uploadResourceFile<TData = FileResponse>(
    resourceId: string,
    requestBodyFileUpload: RequestBodyFileUpload,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  uploadResourceFile<TData = FileResponse>(
    resourceId: string,
    requestBodyFileUpload: RequestBodyFileUpload,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const formData = new FormData();
    formData.append(`file`, requestBodyFileUpload.file);

    if (options?.observe === 'events') {
      return this.http.post<TData>(`${this.baseUrl}/api/resources/${resourceId}/files`, formData, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.post<TData>(`${this.baseUrl}/api/resources/${resourceId}/files`, formData, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.post<TData>(`${this.baseUrl}/api/resources/${resourceId}/files`, formData, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Deletes an existing file from a resource
   * @summary Delete Resource File
   */
  deleteResourceFile<TData = unknown>(
    resourceId: string,
    fileId: string,
    options?: HttpClientBodyOptions,
  ): Observable<TData>;
  deleteResourceFile<TData = unknown>(
    resourceId: string,
    fileId: string,
    options?: HttpClientEventOptions,
  ): Observable<HttpEvent<TData>>;
  deleteResourceFile<TData = unknown>(
    resourceId: string,
    fileId: string,
    options?: HttpClientResponseOptions,
  ): Observable<AngularHttpResponse<TData>>;
  deleteResourceFile<TData = unknown>(
    resourceId: string,
    fileId: string,
    options?: HttpClientObserveOptions,
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.delete<TData>(`${this.baseUrl}/api/resources/${resourceId}/files/${fileId}`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.delete<TData>(`${this.baseUrl}/api/resources/${resourceId}/files/${fileId}`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.delete<TData>(`${this.baseUrl}/api/resources/${resourceId}/files/${fileId}`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
}
